import { useRef, type ReactNode } from 'react';
import {
	AnimatePresence,
	motion,
	useReducedMotion,
	type Transition,
	type Variants,
} from 'framer-motion';
import { cn } from '@/lib/utils';
import {
	PRONTUARIO_BG_CLASS,
	PRONTUARIO_VIEW_TRANSITION_EASE,
	PRONTUARIO_VIEW_TRANSITION_SEC,
} from '../../constants/prontuarioTheme';

type ProntuarioView =
	| 'notes'
	| 'sessions'
	| 'session-detail'
	| 'patient-detail';

type EnterFrom = 'left' | 'right';

const resolveEnterFrom = (
	from: ProntuarioView,
	to: ProntuarioView,
): EnterFrom => {
	if (to === 'patient-detail') return 'right';
	if (from === 'patient-detail') return 'left';
	if (to === 'sessions') return 'left';
	if (from === 'sessions' && to === 'notes') return 'right';
	if (to === 'session-detail') return 'right';
	if (from === 'session-detail') return 'left';
	return 'right';
};

/** Quanto a tela que sai desliza no sentido oposto (parallax estilo iOS). */
const PARALLAX = 30;

const SHADOW_RIGHT = '-8px 0 24px rgba(0,0,0,0.35)';
const SHADOW_LEFT = '8px 0 24px rgba(0,0,0,0.35)';
const SHADOW_NONE = '0px 0 24px rgba(0,0,0,0)';

const layerVariants: Variants = {
	enter: (from: EnterFrom) => ({
		x: from === 'right' ? '100%' : '-100%',
		zIndex: 2,
		boxShadow: from === 'right' ? SHADOW_RIGHT : SHADOW_LEFT,
	}),
	center: (from: EnterFrom) => ({
		x: '0%',
		zIndex: 2,
		boxShadow: from === 'right' ? SHADOW_RIGHT : SHADOW_LEFT,
	}),
	exit: (from: EnterFrom) => ({
		x: from === 'right' ? `-${PARALLAX}%` : `${PARALLAX}%`,
		zIndex: 1,
		boxShadow: SHADOW_NONE,
	}),
};

/** Reduced motion: troca instantânea com crossfade curtíssimo, sem deslocamento. */
const reducedLayerVariants: Variants = {
	enter: { x: '0%', opacity: 0, zIndex: 2 },
	center: { x: '0%', opacity: 1, zIndex: 2 },
	exit: { x: '0%', opacity: 0, zIndex: 1 },
};

/** Escurece a tela que sai conforme some por baixo da que entra. */
const dimVariants: Variants = {
	enter: { opacity: 0 },
	center: { opacity: 0 },
	exit: { opacity: 0.35 },
};

type SessionCallProntuarioViewTransitionProps = {
	view: ProntuarioView;
	renderView: (activeView: ProntuarioView) => ReactNode;
	className?: string;
};

const SessionCallProntuarioViewTransition = ({
	view,
	renderView,
	className,
}: SessionCallProntuarioViewTransitionProps) => {
	const reduce = useReducedMotion();

	// Direção derivada da navegação anterior → atual. Calculada no render porque é
	// determinística e precisa estar disponível para o mount da nova camada e para
	// o `custom` do AnimatePresence (lido pelo variant de saída).
	const prevViewRef = useRef(view);
	const enterFromRef = useRef<EnterFrom>('right');
	if (prevViewRef.current !== view) {
		enterFromRef.current = resolveEnterFrom(prevViewRef.current, view);
		prevViewRef.current = view;
	}
	const enterFrom = enterFromRef.current;

	const transition: Transition = reduce
		? { duration: 0.12, ease: 'easeOut' }
		: {
				duration: PRONTUARIO_VIEW_TRANSITION_SEC,
				ease: [...PRONTUARIO_VIEW_TRANSITION_EASE],
				zIndex: { duration: 0 },
		  };

	return (
		<div
			className={cn('relative min-h-0 flex-1 overflow-hidden', className)}
		>
			<AnimatePresence custom={enterFrom} mode="sync" initial={false}>
				<motion.div
					key={view}
					custom={enterFrom}
					variants={reduce ? reducedLayerVariants : layerVariants}
					initial="enter"
					animate="center"
					exit="exit"
					transition={transition}
					className={cn(
						'absolute inset-0 min-h-0 overflow-hidden',
						PRONTUARIO_BG_CLASS,
					)}
				>
					{renderView(view)}

					{reduce ? null : (
						<motion.div
							variants={dimVariants}
							transition={transition}
							className="pointer-events-none absolute inset-0 bg-black"
							aria-hidden
						/>
					)}
				</motion.div>
			</AnimatePresence>
		</div>
	);
};

export {
	SessionCallProntuarioViewTransition,
	type ProntuarioView as SessionCallProntuarioView,
};
